import { Outlet, Link, useLocation } from 'react-router'
import { useEffect, useState } from 'react'
import { AnalyticsTracker } from '@/components/ui/AnalyticsTracker'

export function PublicLayout() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const location = useLocation()

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search)
    const token = searchParams.get('invite_token')
    if (token) {
      sessionStorage.setItem('pending_invite_token', token)
      window.history.replaceState(null, '', window.location.pathname)
    }
  }, [location.search])

  const navLinks = [
    { to: '/calculator', label: 'Calculator' },
    { to: '/pricing', label: 'Pricing' },
    { to: '/example', label: 'Example' },
  ]

  return (
    <div className="min-h-screen bg-white">
      <AnalyticsTracker />
      {/* Nav */}
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/60">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between" aria-label="Main navigation">
          <Link to="/" className="flex items-center gap-2 font-bold text-slate-900 text-lg">
            <span className="w-8 h-8 rounded-lg bg-brand-600 flex items-center justify-center text-white text-sm font-bold">CL</span>
            ChangeLink
          </Link>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-6">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`text-sm font-medium transition-colors ${
                  location.pathname === link.to
                    ? 'text-brand-600'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <div className="flex items-center gap-3 ml-2 border-l border-slate-200 pl-4">
              <Link
                to="/sign-in"
                className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
              >
                Sign in
              </Link>
              <Link
                to="/pricing"
                className="inline-flex items-center gap-1.5 bg-brand-600 text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-brand-700 transition-colors"
              >
                Get started
              </Link>
            </div>
          </div>

          {/* Mobile menu button */}
          <button
            className="md:hidden p-2 rounded-lg text-slate-600 hover:bg-slate-100 transition-colors"
            onClick={() => { setMobileMenuOpen(!mobileMenuOpen) }}
            aria-expanded={mobileMenuOpen}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? (
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </nav>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-slate-200 bg-white px-4 py-4 flex flex-col gap-2">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => { setMobileMenuOpen(false) }}
                className="py-2 text-sm font-medium text-slate-700 hover:text-brand-600 transition-colors"
              >
                {link.label}
              </Link>
            ))}
            <div className="pt-2 border-t border-slate-100 flex flex-col gap-2">
              <Link
                to="/sign-in"
                onClick={() => { setMobileMenuOpen(false) }}
                className="py-2 text-sm font-medium text-slate-600"
              >
                Sign in
              </Link>
              <Link
                to="/pricing"
                onClick={() => { setMobileMenuOpen(false) }}
                className="block text-center bg-brand-600 text-white text-sm font-medium px-4 py-2.5 rounded-lg hover:bg-brand-700 transition-colors"
              >
                Get started
              </Link>
            </div>
          </div>
        )}
      </header>

      {/* Content */}
      <main>
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-slate-50 py-12 mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-10">
            <div className="col-span-2 md:col-span-1">
              <Link to="/" className="flex items-center gap-2 font-bold text-slate-900 mb-3">
                <span className="w-7 h-7 rounded-md bg-brand-600 flex items-center justify-center text-white text-xs font-bold">CL</span>
                ChangeLink
              </Link>
              <p className="text-xs text-slate-500 leading-relaxed">
                A documentation tool for residential contractors. Not legal advice.
              </p>
            </div>
            <div>
              <h3 className="text-xs font-semibold text-slate-900 uppercase tracking-wider mb-3">Product</h3>
              <ul className="space-y-2">
                {([['Calculator', '/calculator'], ['Pricing', '/pricing'], ['Example', '/example']] as const).map(([label, to]) => (
                  <li key={to}>
                    <Link to={to} className="text-sm text-slate-500 hover:text-slate-900 transition-colors">{label}</Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="text-xs font-semibold text-slate-900 uppercase tracking-wider mb-3">Legal</h3>
              <ul className="space-y-2">
                {([['Privacy', '/privacy'], ['Terms', '/terms'], ['Security', '/security']] as const).map(([label, to]) => (
                  <li key={to}>
                    <Link to={to} className="text-sm text-slate-500 hover:text-slate-900 transition-colors">{label}</Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="text-xs font-semibold text-slate-900 uppercase tracking-wider mb-3">Account</h3>
              <ul className="space-y-2">
                {([['Sign in', '/sign-in'], ['Get started', '/pricing']] as const).map(([label, to]) => (
                  <li key={to}>
                    <Link to={to} className="text-sm text-slate-500 hover:text-slate-900 transition-colors">{label}</Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="pt-6 border-t border-slate-200 flex flex-col sm:flex-row justify-between items-center gap-2">
            <p className="text-xs text-slate-400">
              © {new Date().getFullYear()} ChangeLink. All rights reserved.
            </p>
            <p className="text-xs text-slate-400">
              ChangeLink is a documentation tool, not legal advice.
            </p>
          </div>
        </div>
      </footer>
    </div>
  )
}
